import React, { memo, useCallback, useMemo } from 'react';

import { Box, Link, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import { RadioCellContent } from '@sharedCR/coreDesignSystem/Table/TableCDS/cellContent';
import { TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import PropTypes from 'prop-types';

import {
  AppraisalOrderContactKind,
  DESCRIPTION_PLACEHOLDER,
  EMPTY_CELL_VALUE,
  MISSING_AGENT_NOTE_LINK_TEXT,
  MISSING_AGENT_NOTE_SUFFIX,
  OrderContactSourceLabel,
  PRIMARY_LABEL_TEXT,
} from '../../AppraisalOrderContactsCard.constants';
import { centeredCellStyles } from '../../AppraisalOrderContactsCard.styles';
import { AppraisalOrderContactsCardTestIds } from '../../AppraisalOrderContactsCard.testIds';
import {
  getManualContactFormValues,
  getMissingAgentNote,
  getOrderContactSelectValue,
} from '../../AppraisalOrderContactsCard.utils';
import ManualContactForm from '../ManualContactForm';
import OrderContactActions from '../OrderContactActions';
import OrderContactSelect from '../OrderContactSelect';

import {
  descriptionInputStyles,
  getRowContainerStyles,
  getRowStyles,
  missingAgentNoteStyles,
  roleCellStyles,
  textCellStyles,
} from './OrderContactRow.styles';

const OrderContactRow = ({
  row,
  rowKey,
  slot,
  contact = null,
  options,
  isChanged = false,
  isAgentMissing = false,
  isEditAllowed = false,
  isLoading = false,
  isActionDisabled = false,
  isManualEntryOpen = false,
  isLastRow = false,
  onSelectOption,
  onClearContact,
  onSetPrimary,
  onRemoveContact,
  onDescriptionChange,
  onEditManualEntry,
  onCancelManualEntry,
  onSubmitManualEntry,
  onOpenAgentAndSeller,
}) => {
  const isOtherKind = row.kind === AppraisalOrderContactKind.OTHER;
  const isSelectDisabled = slot.isLocked || !isEditAllowed || isActionDisabled;
  const selectValue = getOrderContactSelectValue({ contact, isManualEntryOpen });
  const email = contact?.email || EMPTY_CELL_VALUE;
  const phone = contact?.phone || EMPTY_CELL_VALUE;
  const isMissingAgentNoteVisible = isAgentMissing && isEditAllowed;

  const manualContactFormValues = useMemo(() => getManualContactFormValues(row), [row]);

  const onSelect = useCallback(
    option => onSelectOption({ rowId: row.id, option }),
    [onSelectOption, row.id],
  );

  const onClear = useCallback(() => onClearContact(row.id), [onClearContact, row.id]);

  const onDescriptionInputChange = useCallback(
    e => onDescriptionChange({ rowId: row.id, description: e.target.value }),
    [onDescriptionChange, row.id],
  );

  const onManualEntrySubmit = useCallback(
    values => onSubmitManualEntry({ rowId: row.id, values }),
    [onSubmitManualEntry, row.id],
  );

  return (
    <Box
      data-testid={AppraisalOrderContactsCardTestIds.getRowId(rowKey)}
      sx={getRowContainerStyles({ isLastRow })}
    >
      <Box sx={getRowStyles({ isManualEntryOpen })}>
        <Box sx={centeredCellStyles}>
          {isEditAllowed ? (
            <RadioCellContent
              dataTestId={AppraisalOrderContactsCardTestIds.getPrimaryRadioId(rowKey)}
              isChecked={row.isPrimary}
              isDisabled={!contact || isActionDisabled}
              itemId={row.id}
              onClick={onSetPrimary}
            />
          ) : (
            row.isPrimary && (
              <LabelCDS
                dataTestId={AppraisalOrderContactsCardTestIds.getPrimaryLabelId(rowKey)}
                size={LabelSize.S}
                title={PRIMARY_LABEL_TEXT}
                variant={LabelVariant.GREEN}
              />
            )
          )}
        </Box>

        <Box sx={roleCellStyles}>
          {isOtherKind ? (
            <TextFieldCDS
              customStyles={descriptionInputStyles}
              dataTestId={AppraisalOrderContactsCardTestIds.getDescriptionInputId(rowKey)}
              disabled={!isEditAllowed || isActionDisabled}
              name={`${rowKey}-description`}
              placeholder={DESCRIPTION_PLACEHOLDER}
              size="small"
              value={row.description}
              onChange={onDescriptionInputChange}
            />
          ) : (
            <Typography color="text.primary" component="p" variant="regular14">
              {slot.role}
            </Typography>
          )}
          {contact?.isManual && (
            <LabelCDS
              dataTestId={AppraisalOrderContactsCardTestIds.getOrderOnlyLabelId(rowKey)}
              size={LabelSize.S}
              title={OrderContactSourceLabel.ORDER_ONLY}
              variant={LabelVariant.GRAY}
            />
          )}
        </Box>

        <OrderContactSelect
          ariaLabel={`${slot.role} contact`}
          dataTestId={AppraisalOrderContactsCardTestIds.getContactSelectId(rowKey)}
          dataTestIdInput={AppraisalOrderContactsCardTestIds.getContactInputId(rowKey)}
          isClearable={!slot.isLocked}
          isDisabled={isSelectDisabled}
          isFieldChanged={isChanged}
          name={`${rowKey}-contact`}
          options={options}
          value={selectValue}
          onClear={onClear}
          onSelect={onSelect}
        />

        <Box sx={textCellStyles}>
          <Typography noWrap color="text.secondary" component="p" title={email} variant="regular14">
            {email}
          </Typography>
        </Box>

        <Box sx={textCellStyles}>
          <Typography noWrap color="text.secondary" component="p" title={phone} variant="regular14">
            {phone}
          </Typography>
        </Box>

        <Box sx={centeredCellStyles}>
          {isEditAllowed && (
            <OrderContactActions
              contact={contact}
              isDisabled={isActionDisabled}
              isLocked={slot.isLocked}
              isOtherKind={isOtherKind}
              isPrimary={row.isPrimary}
              rowId={row.id}
              rowKey={rowKey}
              source={row.source}
              onClearContact={onClearContact}
              onEditManualEntry={onEditManualEntry}
              onOpenAgentAndSeller={onOpenAgentAndSeller}
              onRemoveContact={onRemoveContact}
              onSetPrimary={onSetPrimary}
            />
          )}
        </Box>

        {isMissingAgentNoteVisible && (
          <Typography
            color="warning.dark"
            component="p"
            sx={missingAgentNoteStyles}
            variant="regular12"
          >
            {getMissingAgentNote(slot)}
            <Link
              component="button"
              data-testid={AppraisalOrderContactsCardTestIds.getAgentAndSellerLinkId(rowKey)}
              type="button"
              variant="regular12"
              onClick={onOpenAgentAndSeller}
            >
              {MISSING_AGENT_NOTE_LINK_TEXT}
            </Link>
            {MISSING_AGENT_NOTE_SUFFIX}
          </Typography>
        )}
      </Box>

      {isManualEntryOpen && (
        <ManualContactForm
          fields={slot.manualFields}
          initialValues={manualContactFormValues}
          isEditingSavedContact={!!contact?.isManual}
          isLoading={isLoading}
          role={slot.role}
          onCancel={onCancelManualEntry}
          onSubmit={onManualEntrySubmit}
        />
      )}
    </Box>
  );
};

OrderContactRow.propTypes = {
  row: PropTypes.shape({
    id: PropTypes.string.isRequired,
    kind: PropTypes.string.isRequired,
    source: PropTypes.string.isRequired,
    isPrimary: PropTypes.bool.isRequired,
    description: PropTypes.string,
  }).isRequired,
  rowKey: PropTypes.string.isRequired,
  slot: PropTypes.shape({
    role: PropTypes.string.isRequired,
    isLocked: PropTypes.bool.isRequired,
    manualFields: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  onSelectOption: PropTypes.func.isRequired,
  onClearContact: PropTypes.func.isRequired,
  onSetPrimary: PropTypes.func.isRequired,
  onRemoveContact: PropTypes.func.isRequired,
  onDescriptionChange: PropTypes.func.isRequired,
  onEditManualEntry: PropTypes.func.isRequired,
  onCancelManualEntry: PropTypes.func.isRequired,
  onSubmitManualEntry: PropTypes.func.isRequired,
  onOpenAgentAndSeller: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    fullName: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
    isManual: PropTypes.bool,
    isAgent: PropTypes.bool,
  }),
  isChanged: PropTypes.bool,
  isAgentMissing: PropTypes.bool,
  isEditAllowed: PropTypes.bool,
  isLoading: PropTypes.bool,
  isActionDisabled: PropTypes.bool,
  isManualEntryOpen: PropTypes.bool,
  isLastRow: PropTypes.bool,
};

export default memo(OrderContactRow);
